/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Monday, 9th Sept 2019
 *
 *  Company Locations View
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import { observer } from 'mobx-react';
import MaterialTable from 'material-table';
import { StoreContext } from 'stores/RootStore';

import AddLocation from 'components/Dialogs/AddLocation';

// Material UI Components
import Grid from '@material-ui/core/Grid';
import AddIcon from '@material-ui/icons/AddBox';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import DetailPanelIcon from '@material-ui/icons/ChevronRight';
import EditIcon from '@material-ui/icons/Edit';
import ExportIcon from '@material-ui/icons/SaveAlt';
import FilterIcon from '@material-ui/icons/FilterList';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import NextPageIcon from '@material-ui/icons/ChevronRight';
import PreviousPageIcon from '@material-ui/icons/ChevronLeft';
import ResetSearchIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import SortArrowIcon from '@material-ui/icons/ArrowDownward';
import ThirdStateCheckIcon from '@material-ui/icons/Remove';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import InfoIcon from '@material-ui/icons/Info';
import Button from '@material-ui/core/Button';

class Locations extends React.Component {
  static contextType = StoreContext;

  constructor(props) {
    super(props);
    this.state = {
      open: false,
    };
    this.handleClose = this.handleClose.bind(this);
  }

  setOpen = value => {
    this.setState({
      open: value,
    });
  };

  handleClickOpen = () => {
    this.setOpen(true);
  };

  handleClose = () => {
    this.setOpen(false);
  };

  columns = [
    {
      field: 'id',
      title: 'Id',
      hidden: true,
    },
    {
      field: 'name',
      title: 'Name',
      editable: 'onUpdate',
      removable: false,
      defaultSort: 'asc',
      sorting: false,
    },
    {
      field: 'address.address1',
      title: 'Address',
      editable: 'never',
      removable: false,
      sorting: false,
      hidden: isWidthDown('xs', this.props.width),
    },
    {
      field: 'cityState',
      title: 'City, State',
      editable: 'never',
      removable: false,
      sorting: false,
      hidden: isWidthDown('sm', this.props.width),
    },
    {
      field: 'active',
      title: 'Active',
      removable: false,
      editable: 'onUpdate',
      sorting: false,
      type: 'boolean',
    },
    {
      field: 'primary',
      title: 'Primary',
      removable: false,
      editable: function editable(columnDef, rowData) {
        return !rowData.primary;
      },
      sorting: false,
      type: 'boolean',
    },
  ];

  componentDidMount() {
    // Update AppStore Page View
    this.context.appStore.setView('Locations');
  }

  render() {
    const { classes, theme } = this.props;
    const stores = this.context;
    const { isLocationsLoading, locations, updateLocation, isAdmin } = stores.companyStore;

    let tableEditable = {};
    if (isAdmin) {
      tableEditable = {
        editable: {
          onRowUpdate: (newData, oldData) => updateLocation(newData, oldData),
        },
      };
    }

    return (
      <Grid container direction='column'>
        <Grid item xs={12} md={11} lg={10}>
          <Grid container className={classes.gridContainer}>
            <Grid item xs={12}>
              <Grid container direction='row' justify='flex-end' alignItems='center' className={classes.invitesHeader}>
                <Grid item className={classes.addLocationButtonWrapper}>
                  {isAdmin || false ? (
                    <Button color='primary' variant='outlined' size='small' onClick={this.handleClickOpen}>
                      Add New Location
                    </Button>
                  ) : null}
                </Grid>
              </Grid>
              <MaterialTable
                isLoading={isLocationsLoading}
                data={locations}
                style={{
                  boxShadow: 'none',
                  backgroundColor: 'inherit',
                }}
                columns={this.columns}
                {...tableEditable}
                icons={{
                  Add: React.forwardRef((props, ref) => <AddIcon {...props} className={classes.icon} ref={ref} />),
                  Check: React.forwardRef((props, ref) => <CheckIcon {...props} className={classes.icon} ref={ref} />),
                  Clear: React.forwardRef((props, ref) => <ClearIcon {...props} className={classes.icon} ref={ref} />),
                  Delete: React.forwardRef((props, ref) => (
                    <DeleteIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  DetailPanel: React.forwardRef((props, ref) => (
                    <DetailPanelIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  Edit: React.forwardRef((props, ref) => <EditIcon {...props} className={classes.icon} ref={ref} />),
                  Export: React.forwardRef((props, ref) => (
                    <ExportIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  Filter: React.forwardRef((props, ref) => (
                    <FilterIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  FirstPage: React.forwardRef((props, ref) => (
                    <FirstPageIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  LastPage: React.forwardRef((props, ref) => (
                    <LastPageIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  NextPage: React.forwardRef((props, ref) => (
                    <NextPageIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  PreviousPage: React.forwardRef((props, ref) => (
                    <PreviousPageIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  ResetSearch: React.forwardRef((props, ref) => (
                    <ResetSearchIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  Search: React.forwardRef((props, ref) => (
                    <SearchIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  SortArrow: React.forwardRef((props, ref) => (
                    <SortArrowIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  ThirdStateCheck: React.forwardRef((props, ref) => (
                    <ThirdStateCheckIcon {...props} className={classes.icon} ref={ref} />
                  )),
                  ViewColumn: React.forwardRef((props, ref) => (
                    <ViewColumnIcon {...props} className={classes.icon} ref={ref} />
                  )),
                }}
                options={{
                  loadingType: 'linear',
                  padding: 'dense',
                  search: false,
                  toolbar: false,
                  showTitle: false,
                  header: true,
                  paging: true,
                  grouping: locations.length > 10,
                  filtering: locations.length > 10,
                  emptyRowsWhenPaging: false,
                  pageSize: 10,
                  pageSizeOptions: [10, 25, 50, 100],
                  actionsColumnIndex: -1,
                  headerStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
                  rowStyle: { backgroundColor: theme.palette.background.default },
                  filterCellStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
                  searchFieldStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
                  actionsCellStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
                  cellStyle: { padding: theme.spacing(1), backgroundColor: theme.palette.background.default },
                }}
                localization={{
                  header: {
                    actions: '',
                  },
                  body: {
                    emptyDataSourceMessage: 'No locations to display',
                  },
                }}
                actions={[
                  {
                    icon: () => <InfoIcon className={classes.icon} />,
                    tooltip: 'View Profile',
                    onClick: (event, rowData) => {
                      this.props.history.push(`/location/${rowData.id}/profile`);
                    },
                  },
                ]}
              />
            </Grid>
          </Grid>
        </Grid>
        <AddLocation open={this.state.open} handleClose={this.handleClose} />
      </Grid>
    );
  }
}

Locations.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styles = theme => ({
  gridContainer: {
    paddingBottom: theme.spacing(2),
  },
  addLocationButtonWrapper: {
    marginBottom: theme.spacing(1),
  },
  icon: {
    color: theme.palette.type === 'light' ? 'rgb(128, 128, 128)' : theme.palette.common.white,
  },
});

export default withStyles(styles, { withTheme: true })(withWidth()(observer(Locations)));
